import React, { useState, useEffect } from 'react';
import { ArrowLeft, Feather, ScrollText, Highlighter, Copy, Check } from 'lucide-react';
import api from '../../lib/api';

const TABS = [
  { id: 'portal', label: 'Portal', icon: Feather },
  { id: 'decretos', label: 'Decretos', icon: ScrollText },
  { id: 'resonancias', label: 'Mis Resonancias', icon: Highlighter },
];

const CampusBitacora = ({ onBack }) => {
  const [tab, setTab] = useState('portal');
  const [entries, setEntries] = useState([]);
  const [decretos, setDecretos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/campus/bitacora').then(r => setEntries(r.data.entries || [])).catch(() => {}),
      api.get('/campus/decretos').then(r => setDecretos(r.data.decretos || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="campus-bitacora">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={onBack}
          className="flex items-center gap-2 text-[#5c4a2a]/35 hover:text-[#5c4a2a]/60 transition-colors text-sm mb-8"
          data-testid="bitacora-back-btn">
          <ArrowLeft className="w-4 h-4" /> Volver al jardín
        </button>

        <div className="text-center mb-8">
          <Feather className="w-8 h-8 text-[#5c4a2a]/30 mx-auto mb-3" />
          <h1 className="text-[#5c4a2a]/75 text-2xl font-light">Mi Bitácora</h1>
          <p className="text-[#5c4a2a]/40 text-sm mt-2">Tu espacio íntimo de registro y memoria</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#f0e6d0]/60 rounded-xl p-1 mb-8 border border-[#c9a227]/10" data-testid="bitacora-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const count = t.id === 'portal' ? entries.length : t.id === 'decretos' ? decretos.length : 0;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                  tab === t.id
                    ? 'bg-white shadow-sm text-[#5c4a2a] border border-[#c9a227]/10'
                    : 'text-[#8b7340]/40 hover:text-[#5c4a2a]/60'
                }`}
                data-testid={`bitacora-tab-${t.id}`}>
                <Icon className="w-3.5 h-3.5" />
                {t.label}
                {count > 0 && <span className="text-[10px] opacity-50">({count})</span>}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === 'portal' && <PortalSection entries={entries} />}
        {tab === 'decretos' && <DecretosSection decretos={decretos} />}
        {tab === 'resonancias' && <ResonanciasSection />}
      </div>
    </div>
  );
};

/* ═══ PORTAL — Respuestas del umbral ═══ */
const PortalSection = ({ entries }) => {
  if (entries.length === 0) {
    return (
      <div className="text-center py-16" data-testid="portal-empty">
        <p className="text-[#5c4a2a]/40 text-sm">Tu bitácora se irá llenando cada vez que cruces el umbral.</p>
        <p className="text-[#5c4a2a]/30 text-xs mt-2">Cada pregunta del portal queda guardada aquí como semilla.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="portal-entries">
      {entries.map(entry => (
        <div key={entry.id} className="bg-white border border-[#5c4a2a]/[0.06] rounded-xl p-6 transition-all hover:border-[#c9a227]/15">
          <p className="text-[#c9a227]/70 text-sm italic mb-3">"{entry.question}"</p>
          <p className="text-[#5c4a2a]/55 text-sm leading-relaxed">{entry.response}</p>
          <p className="text-[#5c4a2a]/35 text-xs mt-4">
            {new Date(entry.created_at).toLocaleDateString('es-ES', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </p>
        </div>
      ))}
    </div>
  );
};

/* ═══ DECRETOS — Decretos desbloqueados ═══ */
const DecretosSection = ({ decretos }) => {
  const [copied, setCopied] = useState(null);

  const copyDecree = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  if (decretos.length === 0) {
    return (
      <div className="text-center py-16" data-testid="decretos-empty">
        <p className="text-[#5c4a2a]/40 text-sm">Los decretos se desbloquean al leer el contenido del Campus.</p>
        <p className="text-[#5c4a2a]/30 text-xs mt-2">Cada lectura puede revelar un decreto para ti.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="decretos-entries">
      {decretos.map((d, i) => (
        <div key={d.id} className="relative group" style={{ animationDelay: `${i * 80}ms` }}>
          <div className="absolute inset-0 bg-[#c9a227]/5 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative bg-white border border-[#c9a227]/10 hover:border-[#c9a227]/25 rounded-xl p-6 transition-all">
            <div className="flex items-start justify-between gap-3">
              <p className="text-[#8a6a20] text-lg font-light italic leading-relaxed flex-1 text-center">
                "{d.text}"
              </p>
              <button onClick={() => copyDecree(d.text, d.id)}
                className="text-[#c9a227]/30 hover:text-[#c9a227]/70 transition-colors flex-shrink-0 mt-1"
                data-testid={`copy-decreto-${d.id}`}>
                {copied === d.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {d.unlocked_at && (
              <p className="text-[#5c4a2a]/35 text-xs text-center mt-4">
                Desbloqueado el {new Date(d.unlocked_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ═══ RESONANCIAS — Textos subrayados (próximamente) ═══ */
const ResonanciasSection = () => (
  <div className="text-center py-16" data-testid="resonancias-section">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
      style={{ background: 'radial-gradient(circle, rgba(201,162,39,0.08) 0%, transparent 70%)' }}>
      <Highlighter className="w-6 h-6 text-[#c9a227]/30" />
    </div>
    <p className="text-[#5c4a2a]/40 text-sm font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '16px', fontStyle: 'italic' }}>
      Este espacio está germinando...
    </p>
    <p className="text-[#5c4a2a]/35 text-xs mt-3 max-w-sm mx-auto leading-relaxed">
      Próximamente podrás subrayar textos que te resuenen durante las lecturas
      y guardarlos aquí como semillas de sabiduría personal.
    </p>
  </div>
);

export default CampusBitacora;
